import { SegmentFilter } from '../segmentFilters';
import { StatItemProps } from './SegmentItem';
import { SegmentedBar, StatRow, WIDGET_COLORS, WidgetCard, WidgetTooltip } from './WidgetPrimitives';

export interface AdditionalRequirementsWidgetData {
  type?: string;
  total_count?: number;
  name?: string;
  counts?: {
    complete?: number;
    incomplete?: number;
  };
  percentages?: {
    complete?: number;
    incomplete?: number;
  };
}

// Shared by the bar segment and the stat below it so both toggle the same filter.
const requirementFilter = (type: string, name: string, status: string, label: string): SegmentFilter => ({
  attribute: `requirement_${type.replace('additional_requirement_', '')}_status`,
  value: status,
  group: name,
  label
});

const requirementStat = (
  type: string,
  name: string,
  percent: number,
  status: string,
  label: string,
  color?: string
): StatItemProps => ({
  value: `${percent}%`,
  label,
  color,
  filter: requirementFilter(type, name, status, label)
});

interface AdditionalRequirementsWidgetProps {
  data: AdditionalRequirementsWidgetData;
}

// Built per-widget rather than as a module constant: the heading is the
// requirement's own name, which only arrives with the data.
const tooltip = (name: string) => (
  <WidgetTooltip
    heading={name}
    description="Shows progress towards meeting the selected requirement."
    definitions={[
      {
        term: 'Completed',
        description: 'Educators who have been marked as meeting the requirement.',
        color: WIDGET_COLORS.green
      },
      { term: 'Incomplete', description: 'Educators who have not met the requirement.' }
    ]}
  />
);

const AdditionalRequirementsWidget = ({
  data: { total_count: total = 0,
    type = '',
    name = '',
    counts: { complete: completeCount = 0, incomplete: incompleteCount = 0 } = {},
    percentages: { complete: completePercent = 0, incomplete: incompletePercent = 0 } = {} }
}: AdditionalRequirementsWidgetProps) => (
  <WidgetCard title={name} total={total} tooltip={tooltip(name)}>
    <SegmentedBar
      segments={[
        {
          percent: completePercent,
          color: WIDGET_COLORS.green,
          label: 'Complete',
          count: completeCount,
          filter: requirementFilter(type, name, 'complete', 'Complete')
        }
      ]}
      remainder={{
        label: 'Incomplete',
        percent: incompletePercent,
        count: incompleteCount,
        filter: requirementFilter(type, name, 'incomplete', 'Incomplete')
      }}
    />
    <StatRow
      items={[
        requirementStat(type, name, completePercent, 'complete', 'Complete', WIDGET_COLORS.green),
        requirementStat(type, name, incompletePercent, 'incomplete', 'Incomplete')
      ]}
    />
  </WidgetCard>
);

export default AdditionalRequirementsWidget;
